import type { WorkflowEditorService } from 'api-clients/monolith';
import { useCallback, useEffect, useRef, useState } from 'react';
import { REACT_APP_MONOLITH_BASE_URL } from 'runtimeEnvVars';

import { apiGet, apiPost } from 'utils/axios';

type ReportStart = Parameters<
  typeof WorkflowEditorService.postInternalApiWorkflowEditorRehireCheckReportTests
>[0];
type ReportRunResponse = Awaited<
  ReturnType<
    typeof WorkflowEditorService.getInternalApiWorkflowEditorRehireCheckReportTests
  >
>;
type MatchingStart = Parameters<
  typeof WorkflowEditorService.postInternalApiWorkflowEditorRehireCheckMatchingTests
>[0];
type MatchingRunResponse = Awaited<
  ReturnType<
    typeof WorkflowEditorService.getInternalApiWorkflowEditorRehireCheckMatchingTests
  >
>;
type Start = ReportStart | MatchingStart;
type RunResponse = ReportRunResponse | MatchingRunResponse;
export type ReportTestInput = Pick<
  ReportStart,
  'config' | 'bindings' | 'purpose'
>;
export type MatchingTestInput = Pick<MatchingStart, 'config' | 'input'>;
export type ReportTestTransport = 'report-v1' | 'matching-v2';
export interface ReportTestScope {
  enabled?: boolean;
  accountId: string | number;
  stageExternalId: string;
  funnelExternalId: string;
  configRevision: string | number;
  inputRevision: string | number;
}
export interface ReportTestView<
  Response extends RunResponse = ReportRunResponse,
> {
  status: 'idle' | 'running' | 'stale' | 'complete' | 'refused' | 'expired';
  sources: NonNullable<Response['sources']>;
  result: Response['result'] | null;
  stale: boolean;
  expiresAt: number | null;
}
export type MatchingTestView = ReportTestView<MatchingRunResponse>;
type Controller<
  Input,
  Response extends RunResponse,
> = ReportTestView<Response> & {
  run(input: Input): Promise<void>;
  onInputChange: () => void;
  reset: () => void;
};
interface Request {
  sequence: number;
  scopeKey: string;
  attempt: number;
  busy: boolean;
  controller: AbortController;
  payload?: Start;
  runId?: string;
  expiresAt?: number;
  deadline?: number;
  pollTimer?: ReturnType<typeof setTimeout>;
  expiryTimer?: ReturnType<typeof setTimeout>;
}
const emptyView = (stale = false): ReportTestView<RunResponse> => ({
  status: stale ? 'stale' : 'idle',
  sources: [],
  result: null,
  stale,
  expiresAt: null,
});

// The host increments revisions on every edit and calls reset on discard/reopen.
// Content equality never restores authority to an earlier request.
export function useReportTest(
  scope: ReportTestScope & { transport: 'matching-v2' },
): Controller<MatchingTestInput, MatchingRunResponse>;
export function useReportTest<Transport extends ReportTestTransport>(
  scope: ReportTestScope & { transport: Transport },
): Controller<
  Transport extends 'matching-v2' ? MatchingTestInput : ReportTestInput,
  Transport extends 'matching-v2' ? MatchingRunResponse : ReportRunResponse
>;
export function useReportTest(
  scope: ReportTestScope & { transport?: 'report-v1' },
): Controller<ReportTestInput, ReportRunResponse>;
export function useReportTest(
  scope: ReportTestScope & { transport?: ReportTestTransport },
): Controller<ReportTestInput | MatchingTestInput, RunResponse> {
  const transport = scope.transport ?? 'report-v1';
  const endpoint = `${REACT_APP_MONOLITH_BASE_URL}/internal_api/workflow_editor/rehire_check/${transport === 'matching-v2' ? 'matching_tests' : 'report_tests'}`;
  const resourceKey = JSON.stringify([
    scope.enabled === true,
    scope.accountId,
    scope.stageExternalId,
    scope.funnelExternalId,
  ]);
  const scopeKey = JSON.stringify([
    resourceKey,
    transport,
    scope.configRevision,
    scope.inputRevision,
  ]);
  const resource = useRef(resourceKey);
  const identity = useRef(scopeKey);
  const sequence = useRef(0);
  const current = useRef<Request | null>(null);
  const mounted = useRef(true);
  const [view, setView] = useState<ReportTestView<RunResponse>>(emptyView);

  const cancel = useCallback(() => {
    const request = current.current;
    current.current = null;
    if (request) {
      request.controller.abort();
      clearTimeout(request.pollTimer);
      clearTimeout(request.expiryTimer);
    }
  }, []);

  if (identity.current !== scopeKey) {
    identity.current = scopeKey;
    const stale =
      resource.current === resourceKey &&
      (current.current !== null || view.stale);
    resource.current = resourceKey;
    cancel();
    setView(emptyView(stale));
  }

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      cancel();
    };
  }, [cancel]);

  function owns(request: Request, attempt = request.attempt) {
    return (
      mounted.current &&
      current.current === request &&
      request.scopeKey === identity.current &&
      request.sequence === sequence.current &&
      request.attempt === attempt
    );
  }

  function finish(request: Request, status: 'refused' | 'expired') {
    if (!owns(request)) return;
    const expiresAt = request.expiresAt ?? null;
    cancel();
    setView({ ...emptyView(), status, expiresAt });
  }

  function isCurrent(request: Request, attempt: number) {
    if (!owns(request, attempt)) return false;
    if (
      request.deadline !== undefined &&
      performance.now() >= request.deadline
    ) {
      finish(request, 'expired');
      return false;
    }
    return true;
  }

  function schedulePoll(active: Request) {
    const request = active;
    request.pollTimer = setTimeout(() => {
      if (!owns(request)) return;
      if (document.visibilityState !== 'visible') {
        schedulePoll(request);
        return;
      }
      void send(request);
    }, 2000);
  }

  function accept(active: Request, data: RunResponse) {
    const request = active;
    if (
      !data ||
      !data.runId ||
      data.expiresAt !== request.expiresAt ||
      (request.runId !== undefined && request.runId !== data.runId)
    ) {
      finish(request, 'refused');
      return;
    }
    if (!['queued', 'running', 'complete'].includes(data.status)) {
      finish(request, data.status === 'expired' ? 'expired' : 'refused');
      return;
    }
    request.runId = data.runId;
    request.payload = undefined;
    if (data.status === 'complete' && !data.result) {
      finish(request, 'refused');
      return;
    }
    setView({
      status: data.status === 'complete' ? 'complete' : 'running',
      sources: data.sources ?? [],
      result: data.status === 'complete' ? (data.result ?? null) : null,
      stale: false,
      expiresAt: request.expiresAt ?? null,
    });
    if (data.status !== 'complete') {
      schedulePoll(request);
    }
  }

  async function send(active: Request) {
    const request = active;
    request.attempt += 1;
    const attempt = request.attempt;
    request.busy = true;
    // Keep HTTP status locally: the shared interceptor otherwise flattens 410/403.
    const options = {
      signal: request.controller.signal,
      validateStatus: (status: number) => status !== 401,
    };
    try {
      const response = request.runId
        ? await apiGet(
            `${endpoint}/${encodeURIComponent(request.runId)}`,
            {
              stageExternalId: scope.stageExternalId,
            },
            options,
          )
        : await apiPost(endpoint, request.payload, options);
      if (!isCurrent(request, attempt)) return;
      if (response.status === 404 || response.status === 410) {
        finish(request, 'expired');
      } else if (response.status >= 400 && response.status < 500) {
        finish(request, 'refused');
      } else if (response.status < 200 || response.status >= 300) {
        unavailable();
      } else {
        accept(request, response.data as RunResponse);
      }
    } catch {
      if (isCurrent(request, attempt)) unavailable();
    } finally {
      if (owns(request, attempt)) request.busy = false;
    }
    function unavailable() {
      setView({
        ...emptyView(),
        status: 'refused',
        expiresAt: request.expiresAt ?? null,
      });
      // A failed START is recovered only by an explicit Run using its original payload.
      // GET recovery never issues a new nonce or another provider run.
      if (request.runId) schedulePoll(request);
    }
  }

  async function run(input: ReportTestInput | MatchingTestInput) {
    if (
      scope.enabled !== true ||
      !mounted.current ||
      scopeKey !== identity.current
    )
      return;
    const previous = current.current;
    if (
      previous?.busy ||
      (previous?.pollTimer && previous.runId && view.status !== 'complete')
    )
      return;
    if (previous?.payload && !previous.runId) {
      if (isCurrent(previous, previous.attempt)) await send(previous);
      return;
    }
    cancel();
    sequence.current += 1;
    const request: Request = {
      sequence: sequence.current,
      scopeKey,
      attempt: 0,
      busy: true,
      controller: new AbortController(),
    };
    current.current = request;
    setView({ ...emptyView(), status: 'running' });
    // Snapshot before the first await; mutable form values cannot change START replay.
    const snapshot = JSON.parse(JSON.stringify(input)) as
      | ReportTestInput
      | MatchingTestInput;
    request.attempt += 1;
    const attempt = request.attempt;
    try {
      const issueStartedAt = performance.now();
      const response = await apiPost(
        `${endpoint}/issue`,
        {
          stageExternalId: scope.stageExternalId,
        },
        {
          signal: request.controller.signal,
          validateStatus: (status: number) => status !== 401,
        },
      );
      if (!owns(request, attempt)) return;
      const issued = response.data as { ticket: string; expiresAt: number };
      const reference = response.headers?.['x-report-test-issued-at'];
      const issuedAt =
        typeof reference === 'string' && /^\d+$/.test(reference)
          ? Number(reference)
          : NaN;
      if (
        response.status < 200 ||
        response.status >= 300 ||
        !issued ||
        typeof issued.ticket !== 'string' ||
        !issued.ticket ||
        !Number.isSafeInteger(issued.expiresAt) ||
        !Number.isSafeInteger(issuedAt) ||
        issuedAt <= 0 ||
        issued.expiresAt <= issuedAt
      ) {
        finish(request, 'refused');
        return;
      }
      request.expiresAt = issued.expiresAt;
      // Anchor before dispatch so network delay consumes, rather than renews, the
      // original Redis lifetime. Replays and polling never recalibrate this deadline.
      request.deadline = issueStartedAt + (issued.expiresAt - issuedAt);
      const issuedScope = {
        ticket: issued.ticket,
        stageExternalId: scope.stageExternalId,
        funnelExternalId: scope.funnelExternalId,
      };
      request.payload =
        transport === 'matching-v2'
          ? { ...(snapshot as MatchingTestInput), ...issuedScope }
          : {
              ...(snapshot as ReportTestInput),
              ...issuedScope,
              clientRequestId: `${request.sequence}`,
            };
      if (!isCurrent(request, attempt)) return;
      request.expiryTimer = setTimeout(
        () => finish(request, 'expired'),
        request.deadline - performance.now(),
      );
      await send(request);
    } catch {
      if (owns(request, attempt)) finish(request, 'refused');
    }
  }

  function reset() {
    cancel();
    setView(emptyView());
  }
  function onInputChange() {
    const hadRequest = current.current !== null;
    cancel();
    setView(previous => emptyView(hadRequest || previous.stale));
  }
  return { ...view, run, onInputChange, reset };
}
